import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import AppNavbar from "../../components/Navbar";
import Loading from "../../components/Loading";
import { getImageIcon, getRegionIcon } from "../../utils/iconRegistry";
import api from "../../services/api";
import toast from "react-hot-toast";
import "../../styles/global.css";

const ServersDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [servers, setServers] = useState([]);

  const inFlightRef = React.useRef(false);
  const [hasProvisioning, setHasProvisioning] = useState(false);

  useEffect(() => {
    fetchServers();
  }, []);

  useEffect(() => {
    setHasProvisioning(
      servers.some((s) => s.status === "new" || !s.ip_address)
    );
  }, [servers]);

  useEffect(() => {
    if (!hasProvisioning) return;
    const tick = () => {
      if (document.hidden) return;
      if (inFlightRef.current) return;
      fetchServers();
    };
    const id = setInterval(tick, 7000);
    return () => clearInterval(id);
  }, [hasProvisioning]);

  const fetchServers = async () => {
    try {
      if (inFlightRef.current) return;
      inFlightRef.current = true;
      setLoading(true);
      const response = await api.get("/droplets");
      setServers(response.data || []);
    } catch (error) {
      console.error("Error fetching servers:", error);
      toast.error("Failed to load servers");
    } finally {
      inFlightRef.current = false;
      setLoading(false);
    }
  };

  const formatMemory = (memory) => {
    if (memory >= 1024) {
      return `${(memory / 1024).toFixed(1)} GB`;
    }
    return `${memory} MB`;
  };

  const getStatusColor = (status) => {
    const statusColors = {
      active: "success",
      new: "warning",
      off: "error",
      archive: "error",
    };
    return statusColors[status] || "default";
  };

  // Component for async image/OS icons
  const AsyncImageIcon = ({ image }) => {
    const img = useMemo(
      () => (typeof image === "string" ? { slug: image, name: image } : image),
      [image]
    );
    const imageKey = img?.slug || img?.name;
    const [state, setState] = useState({
      type: "icon",
      Icon: null,
      src: null,
      label: "",
    });
    useEffect(() => {
      let mounted = true;
      (async () => {
        const res = await getImageIcon(img);
        if (mounted) setState(res);
      })();
      return () => (mounted = false);
    }, [imageKey, img]);
    if (state.type === "img" && state.src)
      return (
        <img
          src={state.src}
          alt={state.label}
          className="dashboard-image-icon"
        />
      );
    const I = state.Icon;
    return I ? (
      <I className="dashboard-icon" aria-label={state.label} />
    ) : (
      <></>
    );
  };

  // Component for region flags
  const AsyncRegionIcon = ({ slug }) => {
    const [state, setState] = useState({
      type: "icon",
      Icon: null,
      src: null,
      label: "",
    });
    useEffect(() => {
      let mounted = true;
      (async () => {
        const res = await getRegionIcon(slug);
        if (mounted) setState(res);
      })();
      return () => (mounted = false);
    }, [slug]);
    if (state.type === "img" && state.src)
      return (
        <img src={state.src} alt={state.label} className="region-flag-icon" />
      );
    const I = state.Icon;
    return I ? <I className="region-icon" aria-label={state.label} /> : <></>;
  };

  if (loading && servers.length === 0) return <Loading />;

  return (
    <>
      <AppNavbar />
      <div className="spaces-page main-container">
        <div className="container">
          {/* Page Header */}
          <div className="spaces-page-header">
            <div className="spaces-page-header-content">
              <h1 className="page-title">VPS Servers</h1>
              <p className="page-subtitle">
                High-performance virtual private servers with SSD storage and
                full root access.
              </p>
            </div>
            <button
              className="btn-primary btn-cta"
              onClick={() => navigate("/order")}
            >
              + Create Server
            </button>
          </div>

          {/* Empty State */}
          {servers.length === 0 ? (
            <div className="spaces-empty-state">
              <div className="spaces-empty-icon">
                <svg
                  width="64"
                  height="64"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                >
                  <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
                  <line x1="8" y1="21" x2="16" y2="21" />
                  <line x1="12" y1="17" x2="12" y2="21" />
                </svg>
              </div>
              <h2 className="spaces-empty-title">No servers yet</h2>
              <p className="spaces-empty-desc">
                Get started by creating your first VPS server. Deploy
                applications, host websites, or run workloads with full control
                and flexibility.
              </p>
              <button
                className="btn-secondary"
                onClick={() => navigate("/order")}
              >
                + Create your first server
              </button>
            </div>
          ) : (
            <>
              {/* Servers Grid */}
              <div className="spaces-grid">
                {servers.map((server) => (
                  <div
                    key={server.id}
                    className="space-card"
                    onClick={() => navigate(`/server/${server.droplet_id}`)}
                  >
                    {/* Creating Overlay */}
                    {(server.status === "new" || !server.ip_address) && (
                      <div
                        className="dashboard-creating-overlay"
                        style={{
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          bottom: 0,
                          background: "rgba(255, 255, 255, 0.9)",
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "center",
                          justifyContent: "center",
                          borderRadius: "8px",
                          zIndex: 10,
                        }}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="dashboard-creating-spinner"></div>
                        <div
                          className="dashboard-creating-text"
                          style={{ marginTop: "12px" }}
                        >
                          Creating server...
                        </div>
                      </div>
                    )}

                    {/* Card Header */}
                    <div className="space-card-header">
                      <div className="space-card-icon">
                        <AsyncImageIcon
                          image={server.image_slug.split("-")[0]}
                        />
                      </div>
                      <div className="space-card-status">
                        <span
                          className={`status-dot ${
                            server.status === "active" ? "active" : "inactive"
                          }`}
                        ></span>
                      </div>
                    </div>

                    {/* Card Content */}
                    <div className="space-card-content">
                      <h3 className="space-card-title">{server.name}</h3>
                      <div className="space-card-meta">
                        <div className="space-card-meta-item">
                          <AsyncRegionIcon slug={server.region} />
                          <span>{server.region}</span>
                        </div>
                        <div className="space-card-meta-item">
                          <span
                            className={`badge badge-${getStatusColor(
                              server.status
                            )}`}
                          >
                            {server.status || "active"}
                          </span>
                        </div>
                      </div>

                      {/* Server Specs */}
                      <div className="space-card-endpoint">
                        <div className="space-card-endpoint-label">
                          Configuration
                        </div>
                        <div
                          className="space-card-endpoint-value"
                          style={{ fontSize: "13px" }}
                        >
                          {server.vcpus} vCPU • {formatMemory(server.memory)} •{" "}
                          {server.disk} GB SSD
                        </div>
                      </div>

                      {server.ip_address && (
                        <div className="space-card-endpoint">
                          <div className="space-card-endpoint-label">
                            IP Address
                          </div>
                          <div className="space-card-endpoint-value">
                            {server.ip_address}
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Card Footer */}
                    <div
                      className="dashboard-card-footer p-0"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        className="space-card-action"
                        onClick={() => navigate(`/server/${server.droplet_id}`)}
                      >
                        <span>Manage Server</span>
                        <svg
                          width="14"
                          height="14"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                        >
                          <path d="M5 12h14M12 5l7 7-7 7" />
                        </svg>
                      </button>
                      <div className="dashboard-card-meta">
                        Created{" "}
                        {new Date(
                          server.created_at || Date.now()
                        ).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default ServersDashboard;
